import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import firebase from '../../../utilities/firebase';
import TradeModal from '../../../components/TradeModal';
import ReceiveTradeModal from './ReceiveTradeModal';
import { recalculateInvoiceTax } from '../../../utilities/invoiceUtils';

const USD = (v) =>
  (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function TradesPanel({ deal = {}, disabled, onTradesChange, onDepositCreated }) {
  const stockNumber = deal.car;
  const [tradeIds, setTradeIds] = React.useState(deal.trades || []);
  const [tradeData, setTradeData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editingTrade, setEditingTrade] = React.useState(null);
  const [receivingTrade, setReceivingTrade] = React.useState(null);

  React.useEffect(() => {
    async function fetch() {
      if (!tradeIds.length) { setTradeData([]); setLoading(false); return; }
      const db = firebase.firestore();
      const docs = await Promise.all(tradeIds.map(id => db.doc('trades/' + id).get()));
      const rows = docs.map(doc => {
        const t = doc.data() || {};
        return { id: doc.id, ...t, netTrade: (t.trade || 0) - (t.payoff || 0) };
      });
      setTradeData(rows);
      setLoading(false);
    }
    fetch();
  }, [tradeIds]);

  const totalNet = tradeData.reduce((s, t) => s + (t.netTrade || 0), 0);

  const notifyParent = (data) => {
    if (onTradesChange) onTradesChange(data.reduce((s, t) => s + (t.trade || 0), 0));
  };

  const deleteRow = async (id) => {
    if (!window.confirm('Remove this trade?')) return;
    await firebase.firestore().doc('deals/' + stockNumber).update({ trades: firebase.firestore.FieldValue.arrayRemove(id) });
    await firebase.firestore().collection('trades').doc(id).delete();
    const newTradeIds = tradeIds.filter(x => x !== id);
    const newTradeData = tradeData.filter(x => x.id !== id);
    setTradeIds(newTradeIds);
    setTradeData(newTradeData);
    await recalculateInvoiceTax(stockNumber);
    notifyParent(newTradeData);
  };

  const handleSaved = (saved) => {
    let newData;
    if (editingTrade) {
      newData = tradeData.map(t => t.id === saved.id ? { ...t, ...saved } : t);
      setTradeData(newData);
    } else {
      setTradeIds(prev => [...prev, saved.id]);
      newData = [...tradeData, saved];
      setTradeData(newData);
    }
    notifyParent(newData);
  };

  const openCreate = () => { setEditingTrade(null); setModalOpen(true); };
  const openEdit = (trade) => { setEditingTrade(trade); setModalOpen(true); };

  return (
    <Box>
      {loading ? (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={22} />
        </Box>
      ) : tradeData.length === 0 ? (
        <Box sx={{ px: 2, py: 2 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>No trade-ins on this deal</Typography>
        </Box>
      ) : (
        <Box>
          {tradeData.map((trade, i) => (
            <Box
              key={trade.id}
              sx={{
                px: 2, py: 1.5,
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1,
                borderBottom: i < tradeData.length - 1 ? 1 : 0, borderColor: 'divider',
                cursor: 'pointer', '&:hover': { bgcolor: 'grey.50' },
              }}
              onClick={() => openEdit(trade)}
            >
              <Box sx={{ display: 'flex', gap: 1.25, flex: 1, minWidth: 0 }}>
                <DirectionsCarIcon sx={{ color: 'text.disabled', mt: 0.2, fontSize: 20, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {[trade.year, trade.make, trade.model].filter(Boolean).join(' ') || 'Unknown Vehicle'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    {trade.vin || 'No VIN'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Trade: <strong>{USD(trade.trade)}</strong>
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Payoff: <strong>{USD(trade.payoff)}</strong>
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: trade.netTrade >= 0 ? 'success.main' : 'error.main' }}>
                      Net: {USD(trade.netTrade)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                {!disabled && (
                  <>
                    <Tooltip title="Receive trade into inventory">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => { e.stopPropagation(); setReceivingTrade(trade); }}
                      >
                        <CallReceivedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deleteRow(trade.id); }}>
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </>
                )}
                <ChevronRightIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              </Box>
            </Box>
          ))}
          <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Net Trade Value</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: totalNet >= 0 ? 'success.main' : 'error.main' }}>
              {USD(totalNet)}
            </Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
        <Button size="small" startIcon={<AddIcon />} onClick={openCreate} disabled={disabled}>
          Add Trade-In
        </Button>
      </Box>

      <TradeModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingTrade(null); }}
        stockNumber={stockNumber}
        trade={editingTrade}
        onSaved={handleSaved}
      />

      <ReceiveTradeModal
        open={!!receivingTrade}
        onClose={(newStock, deposit) => {
          setReceivingTrade(null);
          if (deposit && onDepositCreated) onDepositCreated(deposit);
        }}
        trade={receivingTrade}
        currentStock={stockNumber}
      />
    </Box>
  );
}
